import React from 'react'
import { useSnapshot } from 'valtio'
import { notificationsStore, setFatalModal } from '@/store/notifications-store'
import OverlayModal from '@components/shared/OverlayModal'

/**
 * Fatal Error Modal - Blocking modal for fatal errors
 * 
 * Features:
 * - Blocks all interaction (high z-index overlay)
 * - Shows error message and details
 * - Single dismiss button
 * - Only one fatal modal at a time
 */
function FatalErrorModal() {
    // Only render on client to avoid SSR useLayoutEffect warnings
    if (typeof window === 'undefined') {
        return null;
    }
    
    const snap = useSnapshot(notificationsStore)
    const { fatalModal } = snap
    
    const handleDismiss = () => {
        setFatalModal(null)
    }
    
    const formatDetails = (details) => {
        if (!details) return null
        if (typeof details === 'string') return details
        return JSON.stringify(details, null, 2)
    }
    
    return (
        <OverlayModal
            isOpen={!!fatalModal}
            onClose={handleDismiss}
            size="medium"
            closeOnBackdrop={false}
            hideHeader
            hideClose
            maxWidthClass="max-w-2xl"
            contentClassName="border-2 border-red-700 overflow-hidden"
            labelledBy="fatal-modal-title"
            footer={(
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="px-4 py-2 bg-red-700 text-white font-medium rounded-md hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                >
                    Dismiss
                </button>
            )}
        >
            {fatalModal ? (
                <>
                    <div className="flex items-center justify-between px-6 py-4 border-b border-red-200 bg-red-50">
                        <div className="flex items-center gap-3">
                            <svg className="w-6 h-6 text-red-700" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            <h2 id="fatal-modal-title" className="text-xl font-bold text-red-900">
                                Fatal Error
                            </h2>
                        </div>
                    </div>
                    <div className="px-6 py-4">
                        <p className="text-base text-gray-900 mb-4">{fatalModal.message}</p>
                        
                        {fatalModal.details && (
                            <div className="mt-4">
                                <h3 className="text-sm font-semibold text-gray-700 mb-2">Details:</h3>
                                <pre className="bg-gray-100 p-3 rounded text-sm overflow-auto max-h-64 border border-gray-300">
                                    {formatDetails(fatalModal.details)}
                                </pre>
                            </div>
                        )}
                        
                        {fatalModal.timestamp && (
                            <p className="text-xs text-gray-500 mt-4">
                                Time: {new Date(fatalModal.timestamp).toLocaleString('en-GB')}
                            </p>
                        )}
                    </div>
                </>
            ) : null}
        </OverlayModal>
    )
}

export default FatalErrorModal
